import React from 'react';
import MuiBadge, {type BadgeProps as MuiBadgeProps, badgeClasses} from '@mui/material/Badge';
import {useDataProps, useAriaProps} from '@famly/mf_ts-utils_data-aria-props';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';

type BadgeSeverity = 'critical' | 'info' | 'success' | 'warning' | 'upsell' | 'neutral';

export type BadgeProps = Pick<MuiBadgeProps, 'variant' | 'overlap' | 'max' | 'invisible' | 'children' | 'aria-label'> &
    DataProps & {
        /**
         * The content to be displayed in the badge.
         *
         * Only applicable if for the 'standard' variant.
         */
        badgeContent?: number;

        /**
         * The severity of the badge.
         *
         * This will determine the background color of the badge.
         */
        severity?: BadgeSeverity;

        /**
         * Whether the badge should have a white outline.
         */
        outline?: boolean;
    };

export const Badge = (props: BadgeProps) => {
    const dataProps = useDataProps(props);
    const ariaProps = useAriaProps(props);

    const {
        variant,
        overlap,
        max = 999,
        invisible,
        children,
        'aria-label': ariaLabel,
        badgeContent,
        severity = 'critical',
        outline,
    } = props;

    return (
        <MuiBadge
            variant={variant}
            overlap={overlap}
            max={max}
            invisible={invisible}
            children={children}
            aria-label={ariaLabel}
            badgeContent={badgeContent}
            {...dataProps}
            {...ariaProps}
            slotProps={{badge: () => ({'data-e2e-class': 'badge'})}}
            sx={({modernFamlyTheme}) => ({
                [`& .${badgeClasses.badge}`]: {
                    backgroundColor: modernFamlyTheme.colorPalette[getBackgroundColorForSeverity(severity)],
                    border: outline ? `1px solid ${modernFamlyTheme.colorPalette.n0}` : undefined,
                },
            })}
        />
    );
};

const getBackgroundColorForSeverity = (severity: BadgeSeverity): ColorKey => {
    switch (severity) {
        case 'critical':
            return 'r400';
        case 'info':
            return 'b400';
        case 'success':
            return 'g400';
        case 'warning':
            return 'y400';
        case 'upsell':
            return 'o400';
        case 'neutral':
            return 'n300';
        default:
            exhaustiveCheck(severity);
            return 'n300';
    }
};
